import React, { useState, useEffect } from 'react';
import { Copy, Eye, EyeOff, RefreshCw, Save, Shield, Key, Check } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { validatePassword } from '../../../../../utils/validation';

/**
 * Компонент формы генератора паролей
 */
const PasswordGeneratorForm = ({ 
  formData, 
  setFormData, 
  generatedPassword,
  setGeneratedPassword,
  passwordStrength,
  setPasswordStrength,
  showPassword,
  setShowPassword,
  copied,
  setCopied,
  passwordTypes,
  russianWords,
  russianPhrases,
  addPasswordToHistory,
  savePassword
}) => {
  const [saveName, setSaveName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Обработчик изменения полей ввода
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : 
                      type === 'number' ? parseInt(value) || 0 : value;
                      
    setFormData(prev => ({ ...prev, [name]: fieldValue }));
  };
  
  // Генерация случайного пароля
  const generatePassword = () => {
    let charset = '';
    let password = '';
    
    // Определяем набор символов в зависимости от типа пароля
    if (formData.passwordType === 'random') {
      if (formData.includeLowercase) charset += 'abcdefghijklmnopqrstuvwxyz';
      if (formData.includeUppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      if (formData.includeNumbers) charset += '0123456789';
      if (formData.includeSymbols) charset += '!@#$%^&*()_+{}[]|:;<>,.?/~`-=';
      
      // Исключаем похожие символы
      if (formData.excludeSimilarChars) {
        charset = charset.replace(/[ilLI|`1oO0]/g, '');
      }
      
      // Исключаем неоднозначные символы
      if (formData.excludeAmbiguous) {
        charset = charset.replace(/[{}[\]()<>:;.,'"~`\/\\]/g, '');
      }
      
      // Генерируем пароль
      for (let i = 0; i < formData.length; i++) {
        const randomIndex = Math.floor(Math.random() * charset.length);
        password += charset[randomIndex];
      }
    } 
    // Запоминаемый пароль (на основе словаря)
    else if (formData.passwordType === 'memorable') {
      // Получаем случайные слова из словаря
      for (let i = 0; i < formData.wordCount; i++) {
        let word = russianWords[Math.floor(Math.random() * russianWords.length)];
        
        // Меняем регистр, если включена опция
        if (formData.includeRandomCase && Math.random() > 0.5) {
          word = word.charAt(0).toUpperCase() + word.slice(1);
        }
        
        password += (i === 0 ? '' : formData.wordSeparator) + word;
      }
      
      // Добавляем случайные цифры, если включена опция
      if (formData.includeRandomNumbers) {
        password += formData.wordSeparator + Math.floor(Math.random() * 1000);
      }
    } 
    // PIN-код
    else if (formData.passwordType === 'pin') {
      for (let i = 0; i < formData.pinLength; i++) {
        password += Math.floor(Math.random() * 10);
      }
    } 
    // Парольная фраза
    else if (formData.passwordType === 'phrase') {
      // Выбираем случайную фразу
      let phrase = '';
      for (let i = 0; i < formData.phraseLength; i++) {
        const randomPhrase = russianPhrases[Math.floor(Math.random() * russianPhrases.length)];
        phrase += (i === 0 ? '' : ' ') + randomPhrase;
      }
      
      // Меняем регистр, если включена опция
      if (formData.includeRandomCase) {
        phrase = phrase
          .split(' ')
          .map(word => 
            Math.random() > 0.3 
              ? word.charAt(0).toUpperCase() + word.slice(1) 
              : word
          )
          .join(' ');
      }
      
      // Добавляем числа, если включена опция
      if (formData.includeRandomNumbers) {
        phrase += ' ' + Math.floor(Math.random() * 1000);
      }
      
      password = phrase;
    }
    
    setGeneratedPassword(password);
    checkPasswordStrength(password);
    addPasswordToHistory(password, formData.passwordType);
  };
  
  // Проверка надежности пароля
  const checkPasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength(null);
      return;
    }
    
    // Используем существующую функцию проверки пароля
    const result = validatePassword(
      password,
      8, // minLength
      true, // requireLower
      true, // requireUpper
      true, // requireDigit
      true  // requireSpecial
    );
    
    // Оценка эффективной энтропии пароля
    // Энтропия = log2(размер_алфавита) * длина_пароля
    let alphabetSize = 0;
    if (/[a-z]/.test(password)) alphabetSize += 26;
    if (/[A-Z]/.test(password)) alphabetSize += 26;
    if (/[0-9]/.test(password)) alphabetSize += 10;
    if (/[^a-zA-Z0-9]/.test(password)) alphabetSize += 33;
    
    const entropy = Math.log2(Math.max(1, alphabetSize)) * password.length;
    
    // Определение оценки пароля на основе энтропии и проверок
    let score = 0;
    if (entropy < 28) score = 0; // Очень слабый
    else if (entropy < 56) score = 1; // Слабый
    else if (entropy < 80) score = 2; // Средний
    else if (entropy < 112) score = 3; // Сильный
    else score = 4; // Очень сильный
    
    // Корректировка на основе других факторов
    if (password.length < 8) score = Math.min(score, 1);
    if (password.length < 6) score = 0;
    
    // Дополнительная проверка на последовательности и повторения
    const hasSequences = /(?:abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|012|123|234|345|456|567|678|789|987|876|765|654|543|432|321|210)/i.test(password);
    const hasRepeats = /(.)\1{2,}/i.test(password); // 3+ одинаковых символа подряд
    
    if (hasSequences) score = Math.max(0, score - 1);
    if (hasRepeats) score = Math.max(0, score - 1);
    
    // Проверяем, содержит ли пароль распространенные слова
    const commonWords = ['password', 'admin', 'user', 'login', '12345', 'qwerty', 'welcome', 'парол'];
    if (commonWords.some(word => password.toLowerCase().includes(word))) {
      score = Math.max(0, score - 2);
    }
    
    // Формируем результат проверки
    setPasswordStrength({
      score,
      entropy,
      valid: result.valid,
      hasMinLength: result.hasMinLength,
      hasLower: result.hasLower,
      hasUpper: result.hasUpper,
      hasDigit: result.hasDigit,
      hasSpecial: result.hasSpecial,
      feedback: getStrengthFeedback(score)
    });
  };
  
  // Получение рекомендаций по сложности пароля
  const getStrengthFeedback = (score) => {
    switch (score) {
      case 0:
        return "Очень слабый пароль. Его легко взломать. Попробуйте сделать пароль длиннее и сложнее.";
      case 1:
        return "Слабый пароль. Рекомендуется добавить специальные символы и увеличить длину.";
      case 2:
        return "Средний пароль. Добавьте больше символов разного типа для повышения надежности.";
      case 3:
        return "Хороший пароль. Он достаточно надежен для большинства сервисов.";
      case 4:
        return "Отличный пароль! Он очень надежен и устойчив к взлому.";
      default:
        return "";
    }
  };
  
  // Копирование пароля в буфер обмена
  const copyToClipboard = () => {
    if (!generatedPassword) return;
    
    navigator.clipboard.writeText(generatedPassword).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      alert('Не удалось скопировать пароль. Попробуйте выделить его вручную.');
    });
  };
  
  // Обработчик сохранения пароля
  const handleSavePassword = () => {
    if (showSaveDialog) {
      const savedResult = savePassword(saveName || null);
      if (savedResult) {
        alert(`Пароль "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveName('');
    } else {
      setShowSaveDialog(true);
    }
  };
  
  // Генерируем пароль при первом рендере или изменении типа
  useEffect(() => {
    if (!generatedPassword) {
      generatePassword();
    }
  }, []);
  
  // Компонент для отображения силы пароля
  const PasswordStrengthIndicator = ({ strength }) => {
    if (!strength) return null;
    
    const labels = ["Очень слабый", "Слабый", "Средний", "Хороший", "Отличный"];
    const colors = ["#f87171", "#f87171", "#facc15", "#22c55e", "#10b981"];
    
    return (
      <div className="mt-4">
        <div className="flex justify-between mb-1 text-sm">
          <span className="text-gray-700 dark:text-gray-300">Надежность:</span>
          <span className="font-medium" style={{ color: colors[strength.score] }}>
            {labels[strength.score]}
          </span>
        </div>
        <div className="strength-meter">
          <div className={`strength-meter-fill strength-${strength.score}`}></div>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
          {strength.feedback}
        </p>
        <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="grid grid-cols-2 gap-1 mt-2">
            <div className={`flex items-center ${strength.hasMinLength ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
              <Check className={`w-3 h-3 mr-1 ${strength.hasMinLength ? 'opacity-100' : 'opacity-50'}`} />
              <span>Минимум 8 символов</span>
            </div>
            <div className={`flex items-center ${strength.hasLower ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
              <Check className={`w-3 h-3 mr-1 ${strength.hasLower ? 'opacity-100' : 'opacity-50'}`} />
              <span>Строчные буквы</span>
            </div>
            <div className={`flex items-center ${strength.hasUpper ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
              <Check className={`w-3 h-3 mr-1 ${strength.hasUpper ? 'opacity-100' : 'opacity-50'}`} />
              <span>Заглавные буквы</span>
            </div>
            <div className={`flex items-center ${strength.hasDigit ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
              <Check className={`w-3 h-3 mr-1 ${strength.hasDigit ? 'opacity-100' : 'opacity-50'}`} />
              <span>Цифры</span>
            </div>
            <div className={`flex items-center ${strength.hasSpecial ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
              <Check className={`w-3 h-3 mr-1 ${strength.hasSpecial ? 'opacity-100' : 'opacity-50'}`} />
              <span>Спецсимволы</span>
            </div>
            <div className={`flex items-center ${strength.entropy > 50 ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
              <Check className={`w-3 h-3 mr-1 ${strength.entropy > 50 ? 'opacity-100' : 'opacity-50'}`} />
              <span>Высокая энтропия</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Key className="w-6 h-6 text-violet-500" />
        Генератор пароля
      </h2>
      
      {/* Отображение сгенерированного пароля */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
          <label htmlFor="generatedPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Сгенерированный пароль:
          </label>
          <div className="ml-auto text-sm">
            {passwordStrength && (
              <span className="font-semibold" style={{ 
                color: ['#f87171', '#f87171', '#facc15', '#22c55e', '#10b981'][passwordStrength.score] 
              }}>
                {["Очень слабый", "Слабый", "Средний", "Хороший", "Отличный"][passwordStrength.score]}
              </span>
            )}
          </div>
        </div>
        <div className="relative rounded-md shadow-sm">
          <input
            id="generatedPassword"
            type={showPassword ? "text" : "password"}
            value={generatedPassword || ''}
            readOnly
            className="form-input pr-20 font-mono text-base"
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-2">
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
              aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
            <button
              type="button"
              onClick={copyToClipboard}
              className={`text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 ${copied ? 'copy-animation' : ''}`}
              aria-label="Копировать пароль"
              disabled={!generatedPassword}
            >
              {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        {/* Индикатор надежности пароля */}
        <PasswordStrengthIndicator strength={passwordStrength} />
      </div>
      
      {/* Тип пароля */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Тип пароля
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {passwordTypes.map(type => (
            <div
              key={type.id}
              className={`cursor-pointer p-3 rounded-lg border transition-colors ${
                formData.passwordType === type.id 
                  ? 'bg-violet-50 border-violet-300 dark:bg-violet-900/30 dark:border-violet-700' 
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
              onClick={() => setFormData(prev => ({ ...prev, passwordType: type.id }))}
            >
              <div className="font-medium text-gray-900 dark:text-white">{type.name}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{type.description}</div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Настройки в зависимости от типа пароля */}
      {formData.passwordType === 'random' && (
        <div className="space-y-6">
          {/* Длина пароля */}
          <div>
            <div className="flex justify-between mb-1">
              <label htmlFor="length" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Длина пароля: {formData.length}
              </label>
            </div>
            <input
              id="length"
              name="length"
              type="range"
              min="4"
              max="64"
              value={formData.length}
              onChange={handleInputChange}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-violet-600"
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>4</span>
              <span>16</span>
              <span>32</span>
              <span>64</span>
            </div>
          </div>
          
          {/* Настройки наборов символов */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center">
              <input
                id="includeLowercase"
                name="includeLowercase"
                type="checkbox"
                checked={formData.includeLowercase}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <label htmlFor="includeLowercase" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Строчные буквы (a-z)
              </label>
            </div>
            <div className="flex items-center">
              <input
                id="includeUppercase"
                name="includeUppercase"
                type="checkbox"
                checked={formData.includeUppercase}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <label htmlFor="includeUppercase" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Заглавные буквы (A-Z)
              </label>
            </div>
            <div className="flex items-center">
              <input
                id="includeNumbers"
                name="includeNumbers"
                type="checkbox"
                checked={formData.includeNumbers}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <label htmlFor="includeNumbers" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Цифры (0-9)
              </label>
            </div>
            <div className="flex items-center">
              <input
                id="includeSymbols"
                name="includeSymbols"
                type="checkbox"
                checked={formData.includeSymbols}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <label htmlFor="includeSymbols" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Спецсимволы (!@#$...)
              </label>
            </div>
            <div className="flex items-center">
              <input
                id="excludeSimilarChars"
                name="excludeSimilarChars"
                type="checkbox"
                checked={formData.excludeSimilarChars}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <label htmlFor="excludeSimilarChars" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Исключить похожие (Il1|O0)
              </label>
            </div>
            <div className="flex items-center">
              <input
                id="excludeAmbiguous"
                name="excludeAmbiguous"
                type="checkbox"
                checked={formData.excludeAmbiguous}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <label htmlFor="excludeAmbiguous" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Исключить неоднозначные ({}{})
              </label>
            </div>
          </div>
        </div>
      )}
      
      {formData.passwordType === 'memorable' && (
        <div className="space-y-6">
          {/* Количество слов */}
          <div>
            <div className="flex justify-between mb-1">
              <label htmlFor="wordCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Количество слов: {formData.wordCount}
              </label>
            </div>
            <input
              id="wordCount"
              name="wordCount"
              type="range"
              min="2"
              max="8"
              value={formData.wordCount}
              onChange={handleInputChange}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-violet-600"
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>2</span>
              <span>4</span>
              <span>6</span>
              <span>8</span>
            </div>
          </div>
          
          {/* Разделитель */}
          <div>
            <label htmlFor="wordSeparator" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Разделитель слов
            </label>
            <select
              id="wordSeparator"
              name="wordSeparator"
              value={formData.wordSeparator}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="-">Дефис (-)</option>
              <option value=".">Точка (.)</option>
              <option value="_">Подчеркивание (_)</option>
              <option value=" ">Пробел</option>
              <option value=",">Запятая (,)</option>
              <option value="">Нет разделителя</option>
            </select>
          </div>
          
          {/* Дополнительные опции */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center">
              <input
                id="includeRandomCase"
                name="includeRandomCase"
                type="checkbox"
                checked={formData.includeRandomCase}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <label htmlFor="includeRandomCase" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Разный регистр букв
              </label>
            </div>
            <div className="flex items-center">
              <input
                id="includeRandomNumbers"
                name="includeRandomNumbers"
                type="checkbox"
                checked={formData.includeRandomNumbers}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <label htmlFor="includeRandomNumbers" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Добавить случайные цифры
              </label>
            </div>
          </div>
        </div>
      )}
      
      {formData.passwordType === 'pin' && (
        <div className="space-y-6">
          {/* Длина PIN-кода */}
          <div>
            <div className="flex justify-between mb-1">
              <label htmlFor="pinLength" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Длина PIN-кода: {formData.pinLength}
              </label>
            </div>
            <input
              id="pinLength"
              name="pinLength"
              type="range"
              min="4"
              max="12"
              value={formData.pinLength}
              onChange={handleInputChange}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-violet-600"
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>4</span>
              <span>6</span>
              <span>8</span>
              <span>12</span>
            </div>
          </div>
          
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-lg p-3">
            <p className="text-sm text-amber-700 dark:text-amber-300">
              PIN-коды состоят только из цифр и часто используются для банковских карт, телефонов и других устройств. 
              Для онлайн-аккаунтов рекомендуется использовать более сложные пароли.
            </p>
          </div>
        </div>
      )}
      
      {formData.passwordType === 'phrase' && (
        <div className="space-y-6">
          {/* Количество фраз */}
          <div>
            <div className="flex justify-between mb-1">
              <label htmlFor="phraseLength" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Количество фраз: {formData.phraseLength}
              </label>
            </div>
            <input
              id="phraseLength"
              name="phraseLength"
              type="range"
              min="1"
              max="4"
              value={formData.phraseLength}
              onChange={handleInputChange}
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-violet-600"
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>1</span>
              <span>2</span>
              <span>3</span>
              <span>4</span>
            </div>
          </div>
          
          {/* Дополнительные опции */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center">
              <input
                id="includeRandomCase"
                name="includeRandomCase"
                type="checkbox"
                checked={formData.includeRandomCase}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <label htmlFor="includeRandomCase" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Разный регистр букв
              </label>
            </div>
            <div className="flex items-center">
              <input
                id="includeRandomNumbers"
                name="includeRandomNumbers"
                type="checkbox"
                checked={formData.includeRandomNumbers}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <label htmlFor="includeRandomNumbers" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Добавить случайные цифры
              </label>
            </div>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-lg p-3">
            <p className="text-sm text-green-700 dark:text-green-300">
              Парольные фразы легче запомнить и могут быть очень надежными благодаря своей длине. 
              Они идеально подходят для важных аккаунтов и мастер-паролей.
            </p>
          </div>
        </div>
      )}
      
      {/* Диалог сохранения пароля */}
      {showSaveDialog && (
        <div className="mt-4 mb-6 p-4 bg-violet-50 dark:bg-violet-900/20 border border-violet-100 dark:border-violet-800 rounded-lg">
          <div className="flex items-center">
            <input
              type="text"
              placeholder="Введите название для сохранения"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              className="form-input flex-grow mr-3"
              autoFocus
            />
            <Button
              type="button"
              onClick={handleSavePassword}
              primary
              size="sm"
            >
              Сохранить
            </Button>
          </div>
        </div>
      )}
      
      {/* Кнопки действий */}
      <div className="mt-8 flex flex-wrap gap-4">
        <Button
          type="button"
          onClick={generatePassword}
          primary
          icon={<RefreshCw className="w-5 h-5" />}
          size="lg"
        >
          Сгенерировать новый пароль
        </Button>
        <Button
          type="button"
          onClick={copyToClipboard}
          variant="secondary"
          icon={<Copy className="w-5 h-5" />}
          disabled={!generatedPassword}
          size="lg"
        >
          {copied ? "Скопировано!" : "Копировать"}
        </Button>
        <Button
          type="button"
          onClick={handleSavePassword}
          variant="secondary"
          icon={<Save className="w-5 h-5" />}
          disabled={!generatedPassword}
          size="lg"
        >
          Сохранить
        </Button>
      </div>
    </div>
  );
};

export default PasswordGeneratorForm;